import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Checkbox, message, Alert } from 'antd';
import Button from '../../UI/buttons/Button';
import {
    getDemoPreflight,
    getDemoStatus,
    createDemoData,
    flushDemoData,
    DemoJob,
    DemoBatchInfo,
} from '../../api/demo';

// Styled components reused from the old DemoDataSettings page
const JobsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0 2rem 0;
  max-height: 20rem;
  overflow: auto;
`;

const JobItem = styled.li`
  display: flex;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #dae5ec;
`;

const JobTitle = styled.span`
  margin-left: 1rem;
`;

const ActionsRow = styled.div`
  margin-top: 2rem;
  display: flex;
  gap: 1rem;
`;

const DemoInfoBox = styled.div`
  margin: 1rem 0;
  padding: 1rem;
  background: #f6f8fa;
  border-radius: 0.5rem;
  border-left: 4px solid #0969da;
`;

const DemoDataSection: React.FC = () => {
    const { t } = useTranslation();

    const [jobs, setJobs] = useState<DemoJob[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [hasDemoData, setHasDemoData] = useState(false);
    const [demoBatchInfo, setDemoBatchInfo] = useState<DemoBatchInfo | null>(null);
    const [canCreateDemo, setCanCreateDemo] = useState(false);
    const [loading, setLoading] = useState(false);

    const refreshStatus = useCallback(async () => {
        const status = await getDemoStatus();
        if (status) {
            setHasDemoData(status.has_demo_data);
            // Update demoBatchInfo if demo data exists
            if (status.has_demo_data && status.batches.length > 0) {
                const batch = status.batches[0]; // Use the first batch
                setDemoBatchInfo({
                    id: batch.id,
                    created_at: batch.created_at,
                    created_by: batch.created_by,
                    object_count: batch.object_count,
                });
            } else {
                setDemoBatchInfo(null);
            }
        } else {
            message.error(t('ERROR_LOADING_DATA', 'Error loading data'));
        }
    }, [t]);

    const loadPreflight = useCallback(async () => {
        try {
            const data = await getDemoPreflight();
            setJobs(data.available_jobs);
            setCanCreateDemo(data.can_create_demo);
            setHasDemoData(data.has_demo_data);
            setDemoBatchInfo(data.demo_batch_info);
        } catch (err) {
            console.error(err);
            message.error(t('ERROR_LOADING_DATA', 'Error loading data'));
        }
    }, [t]);

    useEffect(() => {
        refreshStatus();
        loadPreflight();
    }, [refreshStatus, loadPreflight]);

    const handleToggle = (id: string) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const handleSeed = async () => {
        if (hasDemoData) {
            message.error(
                t(
                    'DEMO_DATA_EXISTS_ERROR',
                    'Demo data already exists. Please remove existing demo data first.'
                )
            );
            return;
        }

        setLoading(true);
        try {
            await createDemoData({ job_ids: selectedIds });
            message.success(t('DEMO_DATA_CREATION_STARTED', 'Demo data creation started'));
            setSelectedIds([]);

            // Allow async task to finish then refresh
            setTimeout(async () => {
                await refreshStatus();
                await loadPreflight();
            }, 1000);
        } catch (err: any) {
            console.error(err);
            const errorMessage =
                err.response?.data?.error ||
                t('ERROR_CREATING_DEMO_DATA', 'Error creating demo data');
            message.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleFlush = async () => {
        if (!hasDemoData) {
            message.error(t('NO_DEMO_DATA_ERROR', 'No demo data found to remove.'));
            return;
        }

        setLoading(true);
        try {
            await flushDemoData();
            message.success(t('DEMO_DATA_FLUSH_STARTED', 'Demo data removal started'));

            // Allow async task to finish then refresh
            setTimeout(async () => {
                await refreshStatus();
                await loadPreflight();
            }, 1000);
        } catch (err: any) {
            console.error(err);
            const errorMessage =
                err.response?.data?.error ||
                t('ERROR_FLUSHING_DEMO_DATA', 'Error flushing demo data');
            message.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString();
    };

    return (
        <>
            {/* Demo data status */}
            {hasDemoData && demoBatchInfo && (
                <DemoInfoBox>
                    <h4>{t('EXISTING_DEMO_DATA', 'Existing Demo Data')}</h4>
                    <p>
                        <strong>{t('CREATED_ON', 'Created on')}:</strong>{' '}
                        {formatDate(demoBatchInfo.created_at)}
                    </p>
                    {demoBatchInfo.created_by && (
                        <p>
                            <strong>{t('CREATED_BY', 'Created by')}:</strong>{' '}
                            {demoBatchInfo.created_by}
                        </p>
                    )}
                    <p>
                        <strong>{t('OBJECTS_COUNT', 'Objects')}:</strong>{' '}
                        {demoBatchInfo.object_count}
                    </p>
                </DemoInfoBox>
            )}

            {/* Alerts for various states */}
            {hasDemoData && (
                <Alert
                    type="info"
                    message={t(
                        'DEMO_DATA_EXISTS_TITLE',
                        'Demo Data Already Exists'
                    )}
                    description={t(
                        'DEMO_DATA_EXISTS_DESCRIPTION',
                        'You already have demo data. Remove the existing data before creating new demo data.'
                    )}
                    style={{ margin: '1rem 0' }}
                />
            )}

            {jobs.length === 0 ? (
                <Alert
                    type="warning"
                    message={t('NO_ATS_JOBS_TITLE', 'No ATS Jobs Available')}
                    description={t(
                        'NO_ATS_JOBS_DESCRIPTION',
                        'No ATS jobs available. Create at least one job in your ATS first.'
                    )}
                    style={{ margin: '1rem 0' }}
                />
            ) : !canCreateDemo && !hasDemoData ? (
                <Alert
                    type="warning"
                    message={t(
                        'CANNOT_CREATE_DEMO_TITLE',
                        'Cannot Create Demo Data'
                    )}
                    description={t(
                        'CANNOT_CREATE_DEMO_DESCRIPTION',
                        'Unable to create demo data. Please check your ATS integration and ensure you have available jobs.'
                    )}
                    style={{ margin: '1rem 0' }}
                />
            ) : !hasDemoData ? (
                <>
                    <p>
                        {t(
                            'SELECT_JOBS_FOR_DEMO',
                            'Select the job(s) you want demo candidates for'
                        )}
                        :
                    </p>
                    <JobsList>
                        {jobs.map((job) => (
                            <JobItem key={job.id}>
                                <Checkbox
                                    checked={selectedIds.includes(job.id)}
                                    onChange={() => handleToggle(job.id)}
                                    disabled={hasDemoData}
                                />
                                <JobTitle>
                                    {job.name}
                                    {job.location ? ` (${job.location})` : ''}
                                </JobTitle>
                            </JobItem>
                        ))}
                    </JobsList>
                </>
            ) : null}

            {/* Action buttons */}
            <ActionsRow>
                <Button
                    type="primary"
                    disabled={
                        !canCreateDemo ||
                        selectedIds.length === 0 ||
                        loading ||
                        hasDemoData
                    }
                    loading={loading}
                    onClick={handleSeed}
                >
                    {t('GENERATE_DEMO_DATA', 'Generate demo data')}
                </Button>
                <Button
                    danger
                    disabled={!hasDemoData || loading}
                    loading={loading}
                    onClick={handleFlush}
                >
                    {t('REMOVE_DEMO_DATA', 'Remove demo data')}
                </Button>
            </ActionsRow>
        </>
    );
};

export default DemoDataSection; 